import axios from "axios";
import { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import { FaArrowLeft, FaTrash } from "react-icons/fa";
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import AsyncSelect from "react-select/async";
import SupplierFormModal from "../components/modals/SupplierFormModal";
import SuccessModal from "../components/modals/SuccessModal";
import { AuthContext } from "../context/AuthContext";
import "../styles/PurchaseOrderForm.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function PurchaseOrderForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const { po_number } = useParams();
  const [searchParams] = useSearchParams();
  const { userData } = useContext(AuthContext);

  // Determine mode from URL path or query param
  const isEditPath = location.pathname.endsWith("/edit");
  const isCreatePath = location.pathname.includes("/create");
  const mode =
    searchParams.get("mode") ||
    (isEditPath ? "edit" : isCreatePath ? "create" : po_number === "new" ? "create" : "view");
  const isEditing = mode === "edit";
  const isViewing = mode === "view";
  const isCreating = mode === "create" || po_number === "new";

  const [error, setError] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [poItems, setPoItems] = useState([]);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [supplierFormData, setSupplierFormData] = useState({
    supplier_name: "",
    email: "",
    phone: "",
    countryCode: "+94",
    address: "",
    brn: "",
    vat: "",
    bank_name: "",
    branch: "",
    account_number: "",
    payment_terms: "Cash",
    credit_period: "",
    advance_percentage: "",
    currency_id: "",
  });
  // Raw materials selection states
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [materialQuantity, setMaterialQuantity] = useState("");
  const [materialUnitPrice, setMaterialUnitPrice] = useState("");
  const [formData, setFormData] = useState({
    attention: "",
    supplier_id: "",
    remark: "",
    status: "Pending",
    tolerance_limit: 0,
    delivery_date: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdPoNumber, setCreatedPoNumber] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");

  // Fetch raw materials on component mount
  useEffect(() => {
    const fetchRawMaterials = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/admin/raw-materials/get-all`,
          {
            params: { company_code: userData?.company_code },
          },
        );
        if (response.data.success && Array.isArray(response.data.data)) {
          setRawMaterials(
            response.data.data.map((material) => ({
              value: material.material_id,
              label: `${material.material_name} - ${material.qty_type || 'N/A'}`,
              ...material,
            })),
          );
        }
      } catch (error) {
        console.error("Error fetching raw materials:", error);
      }
    };

    if (userData?.company_code) {
      fetchRawMaterials();
    }
  }, [userData?.company_code]);

  // Fetch currencies
  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/currencies/get-currencies`,
        {
          params: { company_code: userData?.company_code },
        },
      );
      if (response.data.success && Array.isArray(response.data.currencies)) {
        setCurrencies(response.data.currencies);
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  };

  // Load raw materials (searchable)
  const loadRawMaterialOptions = async (inputValue) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/raw-materials/get-all`,
        {
          params: { company_code: userData?.company_code },
        },
      );

      let materials = [];
      if (response.data.success && Array.isArray(response.data.data)) {
        materials = response.data.data;
      }

      // Client-side filtering by material name or quantity type
      const q = (inputValue || "").toString().toLowerCase();
      const filtered = materials.filter((material) => {
        const name = (material.material_name || "").toString().toLowerCase();
        const qty_type = (material.qty_type || "").toString().toLowerCase();
        return name.includes(q) || qty_type.includes(q);
      });

      return filtered.map((material) => ({
        value: material.material_id,
        label: `${material.material_name} - ${material.qty_type || 'N/A'}`,
        ...material,
      }));
    } catch (err) {
      console.error("Error loading raw materials:", err);
      return [];
    }
  };

  // Fetch suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/admin/suppliers/get-suppliers`,
          {
            params: { company_code: userData?.company_code },
          },
        );
        setSuppliers(response.data.suppliers || []);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
        setError("Error fetching suppliers");
      }
    };

    if (userData?.company_code) {
      fetchSuppliers();
    }
  }, [userData?.company_code]);

  const refetchSuppliers = async (newSupplierId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/suppliers/get-suppliers`,
        {
          params: { company_code: userData?.company_code },
        },
      );
      setSuppliers(response.data.suppliers || []);
      // Select the newly added supplier
      if (newSupplierId) {
        setFormData((prev) => ({
          ...prev,
          supplier_id: newSupplierId.toString(),
        }));
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  // Load and refresh currencies
  useEffect(() => {
    if (userData?.company_code) {
      fetchCurrencies();
    }
  }, [userData?.company_code]);

  const handleSupplierFormSubmit = async (e) => {
    e.preventDefault();

    // Validate conditional fields
    if (
      supplierFormData.payment_terms === "Credit" &&
      !supplierFormData.credit_period
    ) {
      alert("Credit period is required for credit payment terms");
      return;
    }

    if (
      supplierFormData.payment_terms === "Advance" &&
      !supplierFormData.advance_percentage
    ) {
      alert("Advance percentage is required for advance payment terms");
      return;
    }

    try {
      const cleanPhone = supplierFormData.phone.replace(/\D/g, "");

      const cleanedFormData = {
        ...supplierFormData,
        country_code: supplierFormData.countryCode,
        phone: cleanPhone,
      };
      delete cleanedFormData.countryCode;

      const response = await axios.post(
        `${BASE_URL}/api/admin/suppliers/add-suppliers`,
        {
          ...cleanedFormData,
          company_code: userData?.company_code,
          created_by: userData?.id,
        },
      );

      if (response.data.success) {
        // Reset form
        setSupplierFormData({
          supplier_name: "",
          email: "",
          phone: "",
          countryCode: "+94",
          address: "",
          brn: "",
          vat: "",
          bank_name: "",
          branch: "",
          account_number: "",
          payment_terms: "Cash",
          credit_period: "",
          advance_percentage: "",
          currency_id: "",
        });
        setShowAddSupplierModal(false);
        // Refresh suppliers and select the new one
        refetchSuppliers(response.data.supplier_id);
      }
    } catch (error) {
      alert(
        error.response?.data?.message ||
          "Error saving supplier. Please try again.",
      );
    }
  };

  // Load data for edit/view modes
  useEffect(() => {
    const loadPOData = async () => {
      if ((isEditing || isViewing) && po_number) {
        setIsLoading(true);
        try {
          const response = await axios.get(
            `${BASE_URL}/api/admin/raw-materials/get-raw-materials-po/${po_number}`,
            {
              params: { company_code: userData?.company_code },
            },
          );

          if (response.data.success) {
            setFormData({
              attention: response.data.header.attention || "",
              supplier_id: response.data.header.supplier_id?.toString() || "",
              remark: response.data.header.remark || "",
              status: response.data.header.status || "Pending",
              tolerance_limit: response.data.header.tolerance_limit || 0,
              delivery_date: response.data.header.delivery_date
                ? response.data.header.delivery_date.slice(0, 10)
                : "",
            });

            const formattedItems = await Promise.all(
              (response.data.items || []).map(async (item) => {
                // Always fetch available stock from backend endpoint
                try {
                  const stockRes = await axios.get(
                    `${BASE_URL}/api/admin/raw-materials/get-all`,
                    {
                      params: {
                        company_code: userData?.company_code,
                        material_id: item.material_id,
                      },
                    },
                  );
                  return {
                    ...item,
                    available_stock_qty: stockRes.data?.stock_qty ?? "-",
                    quantity: parseInt(item.quantity) || 0,
                    company_code: userData?.company_code,
                  };
                } catch {
                  return {
                    ...item,
                    available_stock_qty: "-",
                    quantity: parseInt(item.quantity) || 0,
                    company_code: userData?.company_code,
                  };
                }
              }),
            );
            setPoItems(formattedItems);
          } else {
            setError("Failed to load purchase order details");
          }
        } catch (error) {
          setError(`Error loading purchase order: ${error.message}`);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadPOData();
  }, [isEditing, isViewing, po_number, userData?.company_code]);

  // Handle raw material selection
  const handleMaterialSelect = (selectedOption) => {
    setSelectedMaterial(selectedOption);
    setMaterialQuantity("");
    setMaterialUnitPrice("");
  };

  // Add material to PO items
  const handleAddMaterial = () => {
    if (!selectedMaterial) {
      showError("Please select a raw material");
      return;
    }

    const quantity = parseFloat(materialQuantity);
    if (!quantity || quantity <= 0) {
      showError("Please enter a valid quantity");
      return;
    }

    const unitPrice = parseFloat(materialUnitPrice);
    if (unitPrice < 0) {
      showError("Unit price must be 0 or greater");
      return;
    }

    const newItem = {
      material_id: selectedMaterial.material_id,
      material_name: selectedMaterial.material_name,
      material_size: selectedMaterial.material_size || "N/A",
      material_qty: quantity,
      qty_type: selectedMaterial.qty_type || "pcs",
      quantity,
      unit_price: unitPrice,
      total_price: quantity * unitPrice,
      company_code: userData?.company_code,
    };

    // Check for duplicate
    const existingItemIndex = poItems.findIndex(
      (item) => item.material_id === newItem.material_id,
    );

    if (existingItemIndex !== -1) {
      // Update quantity and total price
      const updatedItems = [...poItems];
      updatedItems[existingItemIndex].quantity += quantity;
      updatedItems[existingItemIndex].total_price =
        updatedItems[existingItemIndex].quantity *
        updatedItems[existingItemIndex].unit_price;
      setPoItems(updatedItems);
    } else {
      setPoItems([...poItems, newItem]);
    }

    // Reset inputs
    setSelectedMaterial(null);
    setMaterialQuantity("");
    setMaterialUnitPrice("");
    setError("");
  };



  // Helper function to show error modal
  const showError = (message) => {
    setErrorModalMessage(message);
    setShowErrorModal(true);
    setError("");
  };
  const handleRemoveItem = (material_id) => {
    setPoItems(poItems.filter((item) => item.material_id !== material_id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const poData = {
        company_code: userData.company_code,
        supplier_id: parseInt(formData.supplier_id),
        po_type: "raw",
        attention: formData.attention.trim(),
        remark: formData.remark ? formData.remark.trim() : "",
        status: formData.status || "Pending",
        tolerance_limit: parseFloat(formData.tolerance_limit) || 0,
        delivery_date: formData.delivery_date || null,
        items: poItems.map((item) => ({
          material_id: item.material_id,
          material_name: item.material_name,
          material_size: item.material_size,
          material_qty: parseFloat(item.quantity),
          qty_type: item.qty_type,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          company_code: userData.company_code,
        })),
      };

      let response;
       if (isEditing) {
         response = await axios.put(
           `${BASE_URL}/api/admin/raw-materials/update-raw-materials-po/${po_number}`,
           poData,
         );
       } else {
         response = await axios.post(
           `${BASE_URL}/api/admin/raw-materials/create-raw-materials-po`,
           poData,
         );
       }

      if (response.data.success) {
        if (isEditing) {
          navigate(location.state?.from || "/merchandising/raw-materials-po");
        } else {
          // show success modal with PO number returned from backend
          const poNumber = response.data.po_number || "";
          setCreatedPoNumber(poNumber);
          setShowSuccessModal(true);
        }
      } else {
        setError(response.data.message || "Operation failed");
        showError(response.data.message || "Operation failed");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container fluid className="create-po-container">
      <Card className="mb-3">
        <Card.Header className="d-flex create-po-title">
          <h4>
            {isCreating
              ? "Create Raw Material Purchase Order"
              : isEditing
                ? `Edit Raw Material PO: ${po_number}`
                : `Raw Material PO Details - ${po_number}`}
          </h4>
          <Button
            variant="secondary"
            onClick={() =>navigate(-1)}
            className="cpo-back-button"
          >
            <FaArrowLeft />
            Back to List
          </Button>
        </Card.Header>
      </Card>

      {/* Header Information Card */}
      <Card className="mb-4">
        <Card.Header>
          <h4>Header Information</h4>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group controlId="formSupplier">
                  <Form.Label> Supplier </Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select
                      value={formData.supplier_id}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          supplier_id: e.target.value,
                        })
                      }
                      disabled={isViewing}
                      required
                      style={{ flex: 1 }}
                    >
                      <option value="">Select a supplier</option>
                      {suppliers.map((supplier) => (
                        <option
                          key={supplier.supplier_id}
                          value={supplier.supplier_id}
                        >
                          {supplier.supplier_name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  {!isViewing && (
                    <span
                      className="text-primary"
                      onClick={() => setShowAddSupplierModal(true)}
                      title="Add New Supplier"
                      style={{
                        padding: "0.375rem 0.75rem",
                        whiteSpace: "nowrap",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                      }}
                    >
                      + New Supplier
                    </span>
                  )}
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="formAttention">
                  <Form.Label>Attention</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.attention}
                    onChange={(e) =>
                      setFormData({ ...formData, attention: e.target.value })
                    }
                    placeholder="Enter attention name"
                    disabled={isViewing}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="formDeliveryDate">
                  <Form.Label>Delivery Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        delivery_date: e.target.value,
                      })
                    }
                    disabled={isViewing}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="formToleranceLimit">
                  <Form.Label>Tolerance Limit (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    max={100}
                    value={formData.tolerance_limit}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        tolerance_limit: e.target.value,
                      })
                    }
                    placeholder="Enter tolerance %"
                    disabled={isViewing}
                  />
                  <Form.Text className="text-muted">
                    Allowed range: 0 - 100
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formRemark">
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.remark}
                    onChange={(e) =>
                      setFormData({ ...formData, remark: e.target.value })
                    }
                    placeholder="Enter remarks"
                    disabled={isViewing}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Raw Material Information Card */}
      <Card>
        <Card.Header>
          <h4>Raw Material Information</h4>
        </Card.Header>
        <Card.Body>
          {/* Raw Material Search and Add Section for Create/Edit modes */}
          {(isCreating || isEditing) && (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    Select Raw Material <span className="text-danger">*</span>
                  </Form.Label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions={rawMaterials}
                    loadOptions={loadRawMaterialOptions}
                    value={selectedMaterial}
                    onChange={handleMaterialSelect}
                    placeholder="Search material by name or size"
                    isClearable
                    isSearchable
                    menuPlacement="auto"
                    styles={{
                      container: (base) => ({
                        ...base,
                        width: "100%",
                      }),
                      control: (base, state) => ({
                        ...base,
                        minHeight: "38px",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? "#007bff"
                          : state.isFocused
                            ? "#f8f9fa"
                            : "white",
                        color: state.isSelected ? "white" : "#212529",
                      }),
                    }}
                    noOptionsMessage={({ inputValue }) =>
                      !inputValue
                        ? "Type to search materials..."
                        : "No materials found"
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={materialQuantity}
                    onChange={(e) => setMaterialQuantity(e.target.value)}
                    placeholder="Qty"
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Unit Price</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={materialUnitPrice}
                    onChange={(e) => setMaterialUnitPrice(e.target.value)}
                    placeholder="Price"
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button
                  variant="primary"
                  onClick={handleAddMaterial}
                  className="w-100"
                >
                  Add Material
                </Button>
              </Col>
            </Row>
          )}

          <Row className="mb-3">
            <Col>
              <h6>Materials</h6>
              <div className="table-responsive">
                <Table striped bordered hover className="product-info-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Material Name</th>
                      <th>Size</th>
                      <th>Qty Type</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Total Price</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poItems.map((item, index) => (
                      <tr key={item.material_id}>
                        <td>{index + 1}</td>
                        <td>{item.material_name}</td>
                        <td>{item.material_size}</td>
                        <td>{item.qty_type}</td>
                        <td className="number-col">
                          {isViewing ? (
                            item.quantity
                          ) : (
                            <Form.Control
                              type="number"
                              value={item.quantity}
                              min="0.01"
                              step="0.01"
                              onChange={(e) => {
                                const newQuantity =
                                  parseFloat(e.target.value) || 0;
                                const updatedItems = poItems.map((i) =>
                                  i.material_id === item.material_id
                                    ? {
                                        ...i,
                                        quantity: newQuantity,
                                        total_price: newQuantity * i.unit_price,
                                      }
                                    : i,
                                );
                                setPoItems(updatedItems);
                              }}
                              style={{ width: 100 }}
                            />
                          )}
                        </td>
                        <td className="number-col">
                          ${parseFloat(item.unit_price || 0).toFixed(2)}
                        </td>
                        <td className="number-col">
                          ${parseFloat(item.total_price || 0).toFixed(2)}
                        </td>
                        <td>
                          {!isViewing && (
                            <FaTrash
                              onClick={() => handleRemoveItem(item.material_id)}
                              className="po-remove-icon"
                              title="Remove Material"
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-info">
                      <th colSpan="6"></th>
                      <th className="text-end">Total</th>
                      <th className="number-col">
                        $
                        {(
                          poItems.reduce(
                            (sum, item) =>
                              sum + parseFloat(item.unit_price || 0) * parseFloat(item.quantity || 0),
                            0,
                          ) || 0
                        ).toFixed(2)}
                      </th>
                      <th></th>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Action Buttons at the bottom of the page */}
      <div className="d-flex justify-content-end mt-4">
        {!isViewing && (
          <Button
            variant="primary"
            type="submit"
            className="me-2"
            disabled={isLoading}
            onClick={handleSubmit}
          >
            {isEditing
              ? "Update Purchase Order for Raw Materials"
              : "Create Purchase Order for Raw Materials"}
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={() =>
            navigate(location.state?.from || "/merchandising/raw-materials-po")
          }
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
      <SuccessModal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          // Navigate back to list after dismissing
          navigate(location.state?.from || "/merchandising/raw-materials-po");
        }}
        variant="success"
        modalTitle="Purchase Order Created"
        message={
          createdPoNumber ? (
            <span>
              Purchase Order <strong>{createdPoNumber}</strong> created
              successfully.
            </span>
          ) : (
            "Purchase Order created successfully."
          )
        }
      />
      <SuccessModal
        show={showErrorModal}
        onHide={() => {
          setShowErrorModal(false);
        }}
        variant="error"
        modalTitle="Operation Failed"
        message={errorModalMessage}
      />
      <SupplierFormModal
        show={showAddSupplierModal}
        onHide={() => {
          setShowAddSupplierModal(false);
          // Reset form when closing
          setSupplierFormData({
            supplier_name: "",
            email: "",
            phone: "",
            countryCode: "+94",
            address: "",
            brn: "",
            vat: "",
            bank_name: "",
            branch: "",
            account_number: "",
            payment_terms: "Cash",
            credit_period: "",
            advance_percentage: "",
            currency_id: "",
          });
        }}
        onSubmit={handleSupplierFormSubmit}
        editingId={null}
        formData={supplierFormData}
        setFormData={setSupplierFormData}
        currencies={currencies}
        onCurrencyAdded={() => {
          // Refresh currencies after adding new one
          fetchCurrencies();
        }}
      />
    </Container>
  );
}
